import { createApi } from '@reduxjs/toolkit/query/react';

import { PBM_API_URL } from 'api/APIBase';
import { handleError, handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

/**
 * A mapped lock status object.
 * @typedef {Object} MappedLockStatusV2
 * @property {string} pbmId - The PBM ID.
 * @property {Object} member - The member information.
 * @property {Object[]} auths - The authorizations.
 * @property {Object[]} overrides - The overrides.
 */

export const pbmApi = createApi({
    baseQuery: getBaseQuery(PBM_API_URL),
    reducerPath: 'pbmApi',
    endpoints: (build) => ({
        getPBMLockStatus: build.query({
            query: ({ treatmentId }) => `locks/${treatmentId}`,
            transformResponse: (lock) => ({
                lockStatus: lock.lock_status,
                lockedTimestamp: lock.locked_timestamp,
                lockStart: lock.lock_start,
                lockEnd: lock.lock_end,
                supplierNpi: lock.supplier_npi,
                errorMessage: lock.error_message,
            }),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving PBM lock status.', err);
                }
            },
        }),
        getSegmentLockConfig: build.query({
            query: ({ segmentId }) => `lock_configs/${segmentId}`,
            transformResponse: (config) => config.can_set_lock_dates,
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving PBM lock config.', err);
                }
            },
        }),
        checkLockStatusV2: build.query({
            query: ({ treatmentId, payerId }) => ({
                url: `locks/v2/${treatmentId}/check_status`,
                params: {
                    payer_id: payerId,
                },
            }),
            /**
             * Maps a lock status from the API to a lock status object.
             * @param {Object} lockStatus - The lock status object to map.
             * @returns {MappedLockStatusV2} The mapped lock status object.
             */
            transformResponse: (lockStatus) => {
                const { pbm_id: pbmId, ...rest } = lockStatus ?? {};

                return {
                    pbmId,
                    // Include in both the top level and the pbmSpecificData to support raw JSON display
                    ...rest,
                };
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred checking PBM lock status.',
            ),
        }),
    }),
});

export const { useGetPBMLockStatusQuery, useGetSegmentLockConfigQuery, useCheckLockStatusV2Query } =
    pbmApi;
